// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «¿EN QUÉ GABINETE ESTÁS HOY?»: LAS DECISIONES, SIN PANTALLA   (t530, B-2268, 28-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Lo que decide components/PuestoGabinete, en funciones puras (las carga de verdad la guardia
//  backend/services/paciente-en-gabinete.test.js con test-helpers/utilFrontend, así que aquí no se importa nada):
//   · queHacerAlEntrar: al entrar en un ordenador, si se pregunta, si se usa lo que ya sabe el servidor, si se le vuelve a
//     decir lo que este ordenador contestó hoy, o nada (en el móvil ni se llama);
//   · queHacerAlLlegar: cuando llega «el paciente ha pasado a tu gabinete», si se abre la ficha, si sale el aviso en la
//     esquina (hay algo a medio escribir, o la persona apagó «Abrir la ficha sola») o nada (este ordenador no está en ningún
//     gabinete hoy);
//   · lo que se recuerda en ESTE ordenador: lo contestado hoy y «No volver a preguntar en este ordenador», por clínica y
//     persona (dos personas que comparten ordenador contestan cada una lo suyo).

const PREFIJO = 'biango.puestoGabinete.'

export function claveLocal(clinica, agente) {
  const c = parseInt(clinica, 10), a = parseInt(agente, 10)
  return c > 0 && a > 0 ? PREFIJO + c + '.' + a : null
}

function leer(almacen, k) {
  try { return almacen && k ? JSON.parse(almacen.getItem(k) || 'null') : null } catch (_) { return null }
}
function escribir(almacen, k, v) {
  try { if (almacen && k) { if (v == null) almacen.removeItem(k); else almacen.setItem(k, JSON.stringify(v)) } } catch (_) { /* sin almacenamiento */ }
}

// Lo contestado en este ordenador: { fecha: 'AAAA-MM-DD', gabinete_id: n | null, nombre, otros? }.
// t551 · B-2371 · `otros` (solo si los hay): los OTROS gabinetes en los que también está hoy, [{ gabinete_id, nombre }].
export function otrosLimpios(lista, principal = null) {
  const p = parseInt(principal, 10)
  const vistos = new Set()
  const out = []
  for (const o of Array.isArray(lista) ? lista : []) {
    const g = parseInt(o && typeof o === 'object' ? o.gabinete_id : o, 10)
    if (!(g > 0) || g === p || vistos.has(g)) continue
    vistos.add(g)
    out.push({ gabinete_id: g, nombre: o && typeof o === 'object' && o.nombre ? String(o.nombre) : null })
  }
  return out
}
export function leerPuesto(almacen, clave) {
  const p = leer(almacen, clave)
  if (!p || typeof p !== 'object' || !/^\d{4}-\d{2}-\d{2}$/.test(String(p.fecha || ''))) return null
  const g = parseInt(p.gabinete_id, 10)
  const otros = g > 0 ? otrosLimpios(p.otros, g) : []
  return { fecha: p.fecha, gabinete_id: g > 0 ? g : null, nombre: p.nombre ? String(p.nombre) : null, nunca: p.nunca === true, ...(otros.length ? { otros } : {}) }
}
export function guardarPuesto(almacen, clave, puesto) {
  const antes = leerPuesto(almacen, clave)
  escribir(almacen, clave, { ...puesto, nunca: puesto && puesto.nunca != null ? !!puesto.nunca : !!(antes && antes.nunca) })
}
// «No volver a preguntar en este ordenador» (para quien no va a gabinete).
export function nuncaPreguntar(almacen, clave) {
  const p = leerPuesto(almacen, clave)
  return !!(p && p.nunca)
}

const enLista = (gabinetes, id) => Array.isArray(gabinetes) && gabinetes.some((g) => Number(g.id) === Number(id))

// Al entrar (y al volver a la pantalla): { accion, gabinete_id?, propuesto? }.
//   'nada'      → no se pregunta (sin la migración, sin gabinetes, sin permiso para ver fichas, de visita);
//   'usar'      → el servidor ya sabe lo de hoy (datos.actual);
//   'reenviar'  → este ordenador contestó hoy y el servidor no lo tiene (se le vuelve a decir, sin preguntar);
//   'ninguno'   → «No volver a preguntar en este ordenador»: sin gabinete y sin preguntar;
//   'preguntar' → se pregunta, con el propuesto marcado.
export function queHacerAlEntrar({ datos, local, nunca } = {}) {
  const d = datos || {}
  if (!d.disponible || !d.preguntar || !Array.isArray(d.gabinetes) || !d.gabinetes.length || !d.hoy) return { accion: 'nada' }
  // t551 · B-2371 · con los OTROS gabinetes de hoy, si los hay (los que sigue pudiendo elegir).
  const conOtros = (r, lista) => {
    const o = r.gabinete_id == null ? [] : otrosLimpios(lista, r.gabinete_id).filter((x) => enLista(d.gabinetes, x.gabinete_id))
    return o.length ? { ...r, otros: o } : r
  }
  if (d.actual) return conOtros({ accion: 'usar', gabinete_id: d.actual.gabinete_id == null ? null : Number(d.actual.gabinete_id) }, d.actual.otros)
  if (local && local.fecha === d.hoy && (local.gabinete_id == null || enLista(d.gabinetes, local.gabinete_id))) {
    return conOtros({ accion: 'reenviar', gabinete_id: local.gabinete_id == null ? null : Number(local.gabinete_id) }, local.otros)
  }
  if (nunca) return { accion: 'ninguno', gabinete_id: null }
  let propuesto = d.propuesto != null && enLista(d.gabinetes, d.propuesto) ? Number(d.propuesto) : null
  // Si la persona no tiene citas hoy, el que eligió aquí la última vez (un ordenador suele estar siempre en el mismo).
  if (propuesto == null && local && local.gabinete_id != null && enLista(d.gabinetes, local.gabinete_id)) propuesto = Number(local.gabinete_id)
  return { accion: 'preguntar', propuesto }
}

const entero = (v) => { const n = Number(v); return Number.isInteger(n) && n > 0 ? n : null }

// Al llegar «el paciente ha pasado a tu gabinete» por la conexión en directo. → 'nada' | 'ya_abierta' | 'abrir' | 'aviso'.
// t562 · B-2386 (el dueño, 30-oct: «se ha pasado a un paciente y no se ha abierto la ficha»):
//   · `actual` SIN SABER TODAVÍA (null: la pantalla acaba de cargar y aún no ha preguntado lo de hoy, o esa pregunta falló) ya
//     no es «nada»: manda el servidor, que solo lo manda a un ordenador que hoy ha dicho un gabinete. Solo un «Ninguno» sabido
//     (gabinete vacío) lo para;
//   · `aviso.modo === 'aviso'` (el doctor que hoy está en otro gabinete, o una auxiliar que también va a ese): aviso en la
//     esquina con «Abrir ficha», nunca el salto;
//   · con la ficha de ese paciente YA abierta pero en otra pestaña (`enSuHistorial === false`), se pasa a su Historial, salvo
//     con algo a medio escribir, la casilla apagada o si solo era un aviso.
export function queHacerAlLlegar({ actual, abrirFicha, aviso, enEsaFicha, enSuHistorial, sinGuardar } = {}) {
  if (actual && entero(actual.gabinete_id) == null) return 'nada'          // este ordenador ha dicho «Ninguno» hoy
  if (!aviso || entero(aviso.paciente_id) == null || entero(aviso.cita_id) == null) return 'nada'
  const soloAviso = aviso.modo === 'aviso'
  if (enEsaFicha) return (enSuHistorial === false && !soloAviso && abrirFicha !== false && !sinGuardar) ? 'abrir' : 'ya_abierta'
  if (soloAviso) return 'aviso'
  if (abrirFicha === false) return 'aviso'
  if (sinGuardar) return 'aviso'
  return 'abrir'
}

// t562 · ¿Está esa ficha abierta y ADEMÁS en su Historial (la pestaña «tratamientos»)?
export function estaEnSuHistorial(ruta, busqueda, pacienteId) {
  if (!estaEnEsaFicha(ruta, busqueda, pacienteId)) return false
  try { return new URLSearchParams(String(busqueda || '')).get('tab') === 'tratamientos' } catch (_) { return false }
}

// t562 · El aviso a quien ha pasado la cita cuando la ficha no se ha abierto en ningún ordenador (services/pacienteEnGabinete ·
// MOTIVOS_SIN_ABRIR). → el motivo, si es uno de los que se explican; si no, null (y no sale nada).
const MOTIVOS_SIN_ABRIR = ['sin_ficha', 'sin_gabinete', 'sin_ordenador', 'sin_permiso_o_sede', 'sin_conexion']
export function motivoSinAbrir(datos) {
  const m = datos && typeof datos.motivo === 'string' ? datos.motivo : ''
  return MOTIVOS_SIN_ABRIR.includes(m) ? m : null
}

// La ficha del paciente, en la pestaña Historial (en la ficha se llama «tratamientos»).
export function urlFichaHistorial(pacienteId) {
  const p = entero(pacienteId)
  return p ? '/pacientes?paciente=' + p + '&tab=tratamientos' : null
}

// ¿Está ya abierta la ficha de ese paciente en esta pestaña?
export function estaEnEsaFicha(ruta, busqueda, pacienteId) {
  if (String(ruta || '') !== '/pacientes') return false
  try { return new URLSearchParams(String(busqueda || '')).get('paciente') === String(entero(pacienteId)) } catch (_) { return false }
}

// Lo que lleva el evento de la ventana 'biango:paciente-en-gabinete' (lo escucha «¿Grabar consulta?», t529).
export function detalleDelEvento(aviso, accion) {
  const a = aviso || {}
  return {
    citaId: entero(a.cita_id), pacienteId: entero(a.paciente_id),
    gabinete: a.gabinete && entero(a.gabinete.id) ? { id: entero(a.gabinete.id), nombre: a.gabinete.nombre || null } : null,
    abierta: accion === 'abrir' || accion === 'ya_abierta',
  }
}
